/**
 * Club snapshot — contract C5 (docs/night-wheel/2026-09-09-contracts.md).
 *
 * Native -> web `club:snapshot`: every member's last 14 days of swims plus the
 * club's reactions. This file is the ONLY place the shape lives on the web:
 * the types below mirror C5 field for field, and `parseClubSnapshot` is the
 * Zod gate every inbound body passes through (the event detail is untrusted
 * input from the shell, and a fixture is untrusted input from disk).
 *
 * Preset emoji, in C5 order. Never reorder: the picker renders this array.
 */
import { z } from "zod";

export const PRESET_EMOJI = ["👏", "🔥", "💪", "🌊", "😮", "❤️"] as const;
export type PresetEmoji = (typeof PRESET_EMOJI)[number];

/** The CustomEvent name native dispatches on the same channel as
 *  `swimset:club-status-refresh` (WebViewHost.evaluateJavaScript). */
export const CLUB_SNAPSHOT_EVENT = "swimset:club:snapshot";

/** Reactions older than this are hidden on the web (C5); the record stays. */
export const REACTION_TTL_MS = 24 * 60 * 60 * 1000;

const isoString = z.string().min(1).refine((s) => Number.isFinite(Date.parse(s)), "not an ISO date");

export const ClubSetSchema = z.object({
  label: z.string(),
  reps: z.number().int().nonnegative(),
  distanceM: z.number().nonnegative(),
  stroke: z.string(),
});

export const ClubSwimSchema = z.object({
  id: z.string().min(1),
  date: isoString,
  distanceM: z.number().nonnegative(),
  durationSec: z.number().nonnegative(),
  /** null for a swim with no lap events (open water): native always sends the key,
   *  null when it detects no pool length (B-017, one null used to drop the snapshot). */
  poolM: z.number().nonnegative().nullish(),
  sets: z.array(ClubSetSchema),
  /** Build 16 (parity), additive, `v` stays 1: the swim's corrections revision (clubmate: their
   *  LapLabels record's modifiedAt; own: the LapLabelStore envelope's updatedAt). Absent = none. A
   *  change refetches the swim's firehose (swimLoader). */
  labelsRevision: z.string().nullish(),
  /** Build 16, own swims only: "queued" or "rejected" while a correction has not reached the club.
   *  Absent = shared (or nothing to share). A string, not an enum: an unknown value from a newer
   *  shell must not drop the swim (B-017). */
  share: z.string().nullish(),
});

export const ClubWeekTotalsSchema = z.object({
  swims: z.number().int().nonnegative(),
  distanceM: z.number().nonnegative(),
  durationSec: z.number().nonnegative(),
});

export const ClubMemberSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  swims: z.array(ClubSwimSchema),
  weekTotals: ClubWeekTotalsSchema,
});

export const ClubReactionSchema = z.object({
  swimId: z.string().min(1),
  memberId: z.string().min(1),
  emoji: z.enum(PRESET_EMOJI),
  createdAt: isoString,
});

/** Build 16 (Codex P2-1), additive: revision + share state for EVERY own swim with saved corrections,
 *  by uuid, including the ones older than the 14 days `members` carries for self. */
export const OwnLabelsSchema = z.record(z.string(), z.object({ labelsRevision: z.string().nullish(), share: z.string().nullish() }));

export const ClubSnapshotSchema = z.object({
  v: z.literal(1),
  generatedAt: isoString,
  self: z.string().min(1),
  members: z.array(ClubMemberSchema),
  reactions: z.array(ClubReactionSchema),
  ownLabels: OwnLabelsSchema.optional(),
});

export type ClubSet = z.infer<typeof ClubSetSchema>;
export type ClubSwim = z.infer<typeof ClubSwimSchema>;
export type ClubWeekTotals = z.infer<typeof ClubWeekTotalsSchema>;
export type ClubMember = z.infer<typeof ClubMemberSchema>;
export type ClubReaction = z.infer<typeof ClubReactionSchema>;
export type ClubSnapshot = z.infer<typeof ClubSnapshotSchema>;

/** Web -> native `club:react` body (C5). `emoji: null` removes. */
export interface ClubReactBody {
  swimId: string;
  emoji: PresetEmoji | null;
}

/** One element the parser left out: which kind, whose, which id, and the first field
 *  that failed. Carries ids only, never names or swim contents. */
export interface SnapshotDrop {
  what: "member" | "swim" | "reaction" | "weekTotals" | "ownLabels";
  memberId: string;
  id: string;
  field: string;
}

export type ParsedSnapshot = { ok: true; snapshot: ClubSnapshot; dropped: SnapshotDrop[] } | { ok: false; error: string };

/** The envelope stays strict: a wrong version, no self or no arrays is not a C5 body. */
const EnvelopeSchema = z.object({
  v: z.literal(1),
  generatedAt: isoString,
  self: z.string().min(1),
  members: z.array(z.unknown()),
  reactions: z.array(z.unknown()),
  ownLabels: z.unknown().optional(),
});

const MemberHeadSchema = z.object({ id: z.string().min(1), name: z.string(), swims: z.array(z.unknown()) });

const EMPTY_WEEK: ClubWeekTotals = { swims: 0, distanceM: 0, durationSec: 0 };

const firstField = (error: z.ZodError): string => String(error.issues[0]?.path[0] ?? "");

const stringAt = (o: unknown, key: string): string => {
  const v = o && typeof o === "object" ? (o as Record<string, unknown>)[key] : undefined;
  return typeof v === "string" ? v : "";
};

/** Accepts the body object or a JSON string of it (evaluateJavaScript literals
 *  sometimes arrive stringified). Never throws: a bad body is a visible reason.
 *
 *  Element by element (B-017, build 11): one swim, member or reaction that fails its
 *  schema is left out and listed in `dropped`; everything else renders. Before this a
 *  single `poolM: null` rejected the whole body, so the strip never showed and the Club
 *  calendar lost every clubmate swim but their latest. `weekTotals` (not rendered)
 *  falls back to zeros. */
export function parseClubSnapshot(raw: unknown): ParsedSnapshot {
  let body: unknown = raw;
  if (typeof raw === "string") {
    try {
      body = JSON.parse(raw);
    } catch {
      return { ok: false, error: "club:snapshot detail is not JSON" };
    }
  }
  const envelope = EnvelopeSchema.safeParse(body);
  if (!envelope.success) return { ok: false, error: envelope.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("; ") };

  const dropped: SnapshotDrop[] = [];
  const members: ClubMember[] = [];
  for (const rawMember of envelope.data.members) {
    const head = MemberHeadSchema.safeParse(rawMember);
    if (!head.success) {
      const id = stringAt(rawMember, "id");
      dropped.push({ what: "member", memberId: id, id, field: firstField(head.error) });
      continue;
    }
    const memberId = head.data.id;
    const swims: ClubSwim[] = [];
    for (const rawSwim of head.data.swims) {
      const swim = ClubSwimSchema.safeParse(rawSwim);
      if (swim.success) swims.push(swim.data);
      else dropped.push({ what: "swim", memberId, id: stringAt(rawSwim, "id"), field: firstField(swim.error) });
    }
    const week = ClubWeekTotalsSchema.safeParse((rawMember as Record<string, unknown>).weekTotals);
    if (!week.success) dropped.push({ what: "weekTotals", memberId, id: memberId, field: firstField(week.error) });
    members.push({ id: memberId, name: head.data.name, swims, weekTotals: week.success ? week.data : EMPTY_WEEK });
  }
  const reactions: ClubReaction[] = [];
  for (const rawReaction of envelope.data.reactions) {
    const reaction = ClubReactionSchema.safeParse(rawReaction);
    if (reaction.success) reactions.push(reaction.data);
    else dropped.push({ what: "reaction", memberId: stringAt(rawReaction, "memberId"), id: stringAt(rawReaction, "swimId"), field: firstField(reaction.error) });
  }
  const { v, generatedAt, self } = envelope.data;
  // A bad ownLabels map is left out and named; older own rows then read as unrevised, nothing else moves.
  const own = envelope.data.ownLabels === undefined ? null : OwnLabelsSchema.safeParse(envelope.data.ownLabels);
  if (own && !own.success) dropped.push({ what: "ownLabels", memberId: self, id: self, field: firstField(own.error) });
  const snapshot: ClubSnapshot = own?.success ? { v, generatedAt, self, members, reactions, ownLabels: own.data } : { v, generatedAt, self, members, reactions };
  return { ok: true, snapshot, dropped };
}

/** One console line per dropped element (ids only). */
export function formatSnapshotDrop(d: SnapshotDrop): string {
  return `[club] club:snapshot dropped ${d.what} member=${d.memberId} id=${d.id} field=${d.field}`;
}
